import { Mail, MessageSquare, Phone, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MessageTemplate, useDeleteMessageTemplate } from "@/hooks/useOutreach";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EditTemplateDialog } from "./EditTemplateDialog";

const channelIcons: Record<string, typeof Mail> = {
  email: Mail,
  linkedin: MessageSquare,
  sms: MessageSquare,
  whatsapp: MessageSquare,
  call: Phone,
};

interface TemplateCardProps {
  template: MessageTemplate;
}

export function TemplateCard({ template }: TemplateCardProps) {
  const deleteTemplate = useDeleteMessageTemplate();
  const Icon = channelIcons[template.channel] || Mail;

  const handleDelete = () => {
    deleteTemplate.mutate(template.id);
  };

  return (
    <div className="p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group relative">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">{template.name}</span>
        <span className="text-xs text-muted-foreground capitalize ml-auto">
          {template.tone}
        </span>
      </div>
      {template.subject && (
        <p className="text-xs text-muted-foreground mb-1 font-medium">
          Subject: {template.subject}
        </p>
      )}
      <p className="text-xs text-muted-foreground line-clamp-2">
        {template.content}
      </p>
      
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <EditTemplateDialog 
          template={template}
          trigger={
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Edit className="h-3 w-3" />
            </Button>
          }
        />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{template.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
